import {animateByClass} from "../../../../Library/Animation.js";
import {renderSamplePreview} from "../../TemplateFields";
import {updateActionLabel} from "../../Actions/Preview/Synchronizer.js";
import {buildOptionChip, buildTagChip, hasGroupAttributes, selectedOption} from "../../Preview/Selects.js";

const getPreview = () => document.querySelector('.workflow-container .automation.preview');

const getConnectorText = (match) => match === 'all' ? 'AND' : 'OR';

export const getGlobalFilterIndex = (filter) => {
    const configuration = filter.closest('.filter-configuration');

    if (!configuration) {
        return -1;
    }

    const allFilters = Array.from(configuration.querySelectorAll('.automation-filter'));

    return allFilters.indexOf(filter);
};

const resolveFilterPreview = (filter) => {
    const preview = getPreview();

    if (!preview) {
        return null;
    }

    const filterIndex = getGlobalFilterIndex(filter);

    return preview.querySelectorAll('.filter-preview-group ol li')[filterIndex] ?? null;
};

const getPreviewSpans = (filterPreview) => ({
    fieldName:  filterPreview.querySelector(':scope > .field-name'),
    fieldKey:   filterPreview.querySelector(':scope > .field-key'),
    operator:   filterPreview.querySelector(':scope > .operator'),
    fieldValue: filterPreview.querySelector(':scope > .field-value'),
});

const getValueField = (filter) => {
    const condition = filter.querySelector('.condition:not(.key-field)');

    if (!condition) {
        return null;
    }

    const editableTags = condition.querySelector('.editable-tags');

    if (editableTags) {
        return editableTags.querySelector('select');
    }

    return [...condition.querySelectorAll('input, textarea, select')].find((control) => {
        const name = control.getAttribute('name');

        return name !== null && name !== 'operator' && name !== 'scope';
    }) ?? null;
};

const getOffsetText = (compound, offset) => {
    const amount   = compound.querySelector('input')?.value ?? '';
    const selected = selectedOption(offset);

    if (amount === '1' && selected?.dataset.singular) {
        return selected.dataset.singular;
    }

    return getFieldTextualPresentation(offset);
};

const getScopeText = (filter) => {
    const compound = filter.querySelector('.condition:not(.key-field) [data-compound-value]');
    const offset   = compound?.querySelector('select');

    if (offset) {
        return getOffsetText(compound, offset);
    }

    const scope = filter.querySelector('.condition:not(.key-field) [name="scope"]');

    return scope ? getFieldTextualPresentation(scope) : '';
};

const getFieldTextualPresentation = (target) => {
    if (target.nodeName === 'SELECT' && target.multiple) {
        return [...target.selectedOptions].map(option => option.text || option.value).join(', ');
    }

    if (target.nodeName === 'SELECT') {
        const selected = selectedOption(target);

        return selected ? selected.text : '';
    }

    if (target.nodeName === 'INPUT' || target.nodeName === 'TEXTAREA') {
        return target.value;
    }

    return '';
};

const applyValuePreview = (span, target, scope = '') => {
    if (!span) {
        return;
    }

    if (target && target.nodeName === 'SELECT' && target.multiple) {
        span.replaceChildren(
            ...[...target.selectedOptions].map(option => {
                if (option.dataset.tagColor) {
                    return buildTagChip(option);
                }

                if (hasGroupAttributes(option)) {
                    return buildOptionChip(target, option);
                }

                const tag = document.createElement('span');
                tag.className = 'field-value-tag';
                tag.textContent = option.text || option.value;

                return tag;
            }),
        );

        return;
    }

    if (target && target.nodeName === 'SELECT') {
        const selected = selectedOption(target);

        if (selected?.dataset.tagColor) {
            span.replaceChildren(buildTagChip(selected));
            span.classList.remove('field-value-invalid');

            return;
        }

        if (selected?.value && hasGroupAttributes(selected)) {
            span.replaceChildren(buildOptionChip(target, selected));
            span.classList.remove('field-value-invalid');

            return;
        }
    }

    const withScope = (text) => (scope && text !== '' ? `${text} ${scope}` : text);

    span.textContent = withScope(target ? getFieldTextualPresentation(target) : '');
    span.classList.remove('field-value-invalid');

    if (target) {
        renderSamplePreview(target, (output, invalid) => {
            span.textContent = withScope(output);
            span.classList.toggle('field-value-invalid', invalid);
        });
    }
};

const computeKeyPreview = (filter, target) => {
    const compositeWrapper = filter.querySelector('[data-composite-key]');

    if (compositeWrapper) {
        // Parameterized integration fields use the selected key's label template (data-label-template)
        // with values like "Tax Line %s Amount" instead of joining each part
        const keySelect = compositeWrapper.querySelector('select[name="key"]');
        const templates = JSON.parse(compositeWrapper.dataset.labelTemplates || '{}');
        const template  = keySelect ? templates[keySelect.value] : undefined;

        if (template) {
            const scopeSelect = compositeWrapper.querySelector('select[name="scope"]');
            const args        = Array.from(compositeWrapper.querySelectorAll('[data-key-parameter] :is(select, input)'))
                .filter(field => !field.matches('od-select .search input'))
                .map(field => field.value);

            let index = 0;
            const keyLabel = template.replace(/%[ds]/g, () => args[index++] || '#');

            return [scopeSelect ? getFieldTextualPresentation(scopeSelect) : '', keyLabel]
                .filter(label => label !== '')
                .join(' - ')
            ;
        }

        const labels = Array.from(compositeWrapper.querySelectorAll('select, input'))
            .map(part => getFieldTextualPresentation(part))
            .filter(label => label !== '')
        ;

        return labels.join(' - ');
    }

    if (target) {
        return getFieldTextualPresentation(target);
    }

    const keyInput = filter.querySelector('.key-field input, .key-field select');

    return keyInput ? getFieldTextualPresentation(keyInput) : '';
};

const resetAfterFieldName = (filterPreview) => {
    const {fieldKey, operator, fieldValue} = getPreviewSpans(filterPreview);
    const next = fieldKey ?? operator;

    if (next) {
        next.textContent = '...';
    }

    if (fieldKey && operator) {
        operator.textContent = '';
    }

    if (fieldValue) {
        fieldValue.textContent = '';
    }
};

const buildFilterListItem = (filter) => {
    const listItem = document.createElement('li');
    const template = filter.querySelector('template');

    if (template) {
        listItem.appendChild(template.content.cloneNode(true));
    }

    const {fieldName, fieldKey, operator, fieldValue} = getPreviewSpans(listItem);

    const filterField = filter.querySelector('[name="filterFields"]');

    if (fieldName) {
        fieldName.textContent = filterField ? getFieldTextualPresentation(filterField) : '...';
    }

    if (fieldKey) {
        fieldKey.textContent = computeKeyPreview(filter, null);
    }

    const operatorField = filter.querySelector('[name="operator"]');

    if (operator && operatorField) {
        operator.textContent = getFieldTextualPresentation(operatorField);
    }

    applyValuePreview(fieldValue, getValueField(filter), getScopeText(filter));

    return listItem;
};

const rebuildFilterPreview = () => {
    const preview       = getPreview();
    const configuration = document.querySelector('.side-panel .filter-configuration');

    if (!preview || !configuration) {
        return;
    }

    const container          = preview.querySelector('.filter-preview-groups');
    const configurationMatch = configuration.dataset.match || 'all';

    container.dataset.match = configurationMatch;
    container.replaceChildren();

    const groups = configuration.querySelectorAll('.filter-group');

    groups.forEach((group, groupIndex) => {
        if (groupIndex > 0) {
            const groupConnector     = document.createElement('div');
            groupConnector.className = 'filter-preview-group-connector';

            const connectorLabel       = document.createElement('span');
            connectorLabel.textContent = getConnectorText(configurationMatch);

            groupConnector.appendChild(connectorLabel);

            container.appendChild(groupConnector);
        }

        const groupMatch   = group.dataset.match || 'all';
        const groupElement = document.createElement('div');

        groupElement.className    = 'filter-preview-group';
        groupElement.dataset.match = groupMatch;

        const filterList = document.createElement('ol');
        const filters    = group.querySelectorAll('.automation-filter');

        filters.forEach((filter) => {
            filterList.appendChild(buildFilterListItem(filter));
        });

        groupElement.appendChild(filterList);
        container.appendChild(groupElement);
    });

    updateActionLabel(preview);
};

export const animateMatchChange = (event) => {
    const preview = getPreview();

    if (!preview) {
        return;
    }

    const scope      = event.detail?.scope;
    const groupIndex = event.detail?.groupIndex;

    if (scope === 'group' && groupIndex !== undefined) {
        const group = preview.querySelectorAll('.filter-preview-group')[groupIndex];

        if (group) {
            group.classList.add('connector-flip');
        }

        setTimeout(() => {
            rebuildFilterPreview();

            const newGroup = preview.querySelectorAll('.filter-preview-group')[groupIndex];

            if (newGroup) {
                newGroup.classList.add('connector-flip-in');

                setTimeout(() => newGroup.classList.remove('connector-flip-in'), 200);
            }
        }, 200);

        return;
    }

    if (scope === 'configuration') {
        const connectors = preview.querySelectorAll('.filter-preview-group-connector span');

        connectors.forEach(span => span.classList.add('connector-tag-flip'));

        setTimeout(() => {
            rebuildFilterPreview();

            const newConnectors = preview.querySelectorAll('.filter-preview-group-connector span');

            newConnectors.forEach(span => {
                span.classList.add('connector-tag-flip');

                requestAnimationFrame(() => span.classList.remove('connector-tag-flip'));
            });
        }, 200);

        return;
    }

    rebuildFilterPreview();
};

export const removeFilterPreview = async (event) => {
    const preview = getPreview();

    if (!preview) {
        return;
    }

    const filterPreview = preview.querySelectorAll('.filter-preview-group ol li')[event.detail.index];

    if (!filterPreview) {
        rebuildFilterPreview();

        return;
    }

    await animateByClass(filterPreview, 'remove-filter');

    rebuildFilterPreview();
};

export const addFilterPreview = (event) => {
    const isNewGroup = event.detail?.newGroup;

    rebuildFilterPreview();

    const preview = getPreview();

    if (!preview) {
        return;
    }

    if (isNewGroup) {
        const groups   = preview.querySelectorAll('.filter-preview-group');
        const newGroup = groups[groups.length - 1];

        if (newGroup) {
            animateByClass(newGroup, 'add-filter');
        }

        const connectors    = preview.querySelectorAll('.filter-preview-group-connector');
        const lastConnector = connectors[connectors.length - 1];

        if (lastConnector) {
            animateByClass(lastConnector, 'add-filter');
        }

        return;
    }

    const filter        = event.detail.action;
    const filterGroup   = filter.closest('.filter-group');
    const configuration = filterGroup.closest('.filter-configuration');
    const groupIndex    = [...configuration.querySelectorAll('.filter-group')].indexOf(filterGroup);
    const previewGroup  = preview.querySelectorAll('.filter-preview-group')[groupIndex];

    if (previewGroup) {
        const filterItems     = previewGroup.querySelectorAll('ol li');
        const newFilterItem   = filterItems[filterItems.length - 1];

        if (newFilterItem) {
            animateByClass(newFilterItem, 'add-filter');
        }
    }
};

export const removeGroupPreview = async (event) => {
    const preview = getPreview();

    if (!preview) {
        rebuildFilterPreview();

        return;
    }

    const groupIndex = event.detail?.groupIndex;
    const group      = preview.querySelectorAll('.filter-preview-group')[groupIndex];
    const connector  = group?.previousElementSibling;

    if (group) {
        await animateByClass(group, 'remove-filter');
    }

    if (connector?.classList.contains('filter-preview-group-connector')) {
        connector.remove();
    }

    rebuildFilterPreview();
};

export const synchronizeFilters = () => {
    rebuildFilterPreview();
};

export const animateCrossGroupMove = (movedFilter) => {
    const movedIndex = getGlobalFilterIndex(movedFilter);

    rebuildFilterPreview();

    const preview = getPreview();
    const item    = preview?.querySelectorAll('.filter-preview-group ol li')[movedIndex];

    if (item) {
        item.classList.add('target');
        item.addEventListener('animationend', () => item.classList.remove('target'), { once: true });
    }
};

export const synchronizeOnInput = (event) => {
    if (event.target.matches('od-select .search input')) {
        return;
    }

    const filter = event.target.closest('.automation-filter');

    if (!filter) {
        return;
    }

    const filterPreview = resolveFilterPreview(filter);

    if (!filterPreview) {
        return;
    }

    const target = event.target;
    const {fieldName, fieldKey, operator, fieldValue} = getPreviewSpans(filterPreview);

    if (target.getAttribute('name') === 'filterFields') {
        if (fieldName) {
            fieldName.textContent = getFieldTextualPresentation(target);
        }

        resetAfterFieldName(filterPreview);

        return;
    }

    if (target.closest('.key-field')) {
        if (fieldKey) {
            fieldKey.textContent = computeKeyPreview(filter, target);
        }

        return;
    }

    if (target.getAttribute('name') === 'operator') {
        if (operator) {
            operator.textContent = getFieldTextualPresentation(target);
        }

        return;
    }

    if (target.closest('.condition:not(.key-field)')) {
        applyValuePreview(fieldValue, getValueField(filter), getScopeText(filter));
    }
};

export const synchronizeFilterFields = (filter) => {
    const filterPreview = resolveFilterPreview(filter);

    if (!filterPreview) {
        return;
    }

    const {fieldKey, operator, fieldValue} = getPreviewSpans(filterPreview);

    if (fieldKey) {
        fieldKey.textContent = computeKeyPreview(filter, null);
    }

    const operatorField = filter.querySelector('[name="operator"]');

    if (operator && operatorField) {
        operator.textContent = getFieldTextualPresentation(operatorField);
    }

    applyValuePreview(fieldValue, getValueField(filter), getScopeText(filter));
};
